// Shared constants and helpers for the Recruitment page.

// The page only reports on this fiscal year (June 2026 – May 2027).
export const FISCAL_YEAR = 'FY27';

export const SERVICE_LINES = ['TC', 'BC', 'TO'];

export const PROCESS_MODES = [
  { key: 'accumulated', label: 'Accumulated' },
  { key: 'active', label: 'Active' }
];

// Process statuses from the workbook's "LOVs Recruitment" sheet, in funnel order.
export const STATUSES = [
  { key: '0-Screening', label: 'Screening', group: 'Screening' },
  { key: '1-Application Received', label: 'Application received', group: 'Ongoing' },
  { key: '2-Booking Manager Interview', label: 'Booking manager interview', group: 'Ongoing' },
  { key: '3-Pending Manager Interview', label: 'Pending manager interview', group: 'Ongoing' },
  { key: '4-Booking Partner Interview', label: 'Booking partner interview', group: 'Ongoing' },
  { key: '5-Pending Partner Interview', label: 'Pending partner interview', group: 'Ongoing' },
  { key: '6-Pending Proposal Approval', label: 'Pending proposal approval', group: 'Ongoing' },
  { key: '7-Proposal Sent', label: 'Proposal sent', group: 'Ongoing' },
  { key: '8-Hired', label: 'Hired', group: 'Hired' },
  { key: '9-Proposal Rejected by Candidate', label: 'Proposal rejected by candidate', group: 'Abandoned' },
  { key: '10-Transferred to another RC', label: 'Transferred to another RC', group: 'Abandoned' },
  { key: '11-Candidate Retired Application', label: 'Candidate withdrew application', group: 'Abandoned' },
  { key: '12-Rejected by Deloitte', label: 'Rejected by Deloitte', group: 'Abandoned' },
  { key: '13-On Hold', label: 'On hold', group: 'On hold' }
];

const STATUS_BY_KEY = Object.fromEntries(STATUSES.map(s => [s.key, s]));

export const statusInfo = key =>
  STATUS_BY_KEY[key] || { key, label: key || 'No status', group: 'Other' };

export const statusNumber = key => {
  const match = /^(\d+)-/.exec(key || '');
  return match ? match[1] : '';
};

// Every candidate sits in exactly one stage (by current status), so the stage counts add up to the total.
export const STAGES = [
  { key: 'screening', label: 'Screening', from: 0, to: 1 },
  { key: 'manager', label: 'Manager interview', from: 2, to: 3 },
  { key: 'partner', label: 'Partner interview', from: 4, to: 5 },
  { key: 'proposal', label: 'Proposal', from: 6, to: 7 },
  { key: 'hired', label: 'Hired', from: 8, to: 8 },
  { key: 'closed', label: 'Not proceeding', from: 9, to: 12 },
  { key: 'onhold', label: 'On hold', from: 13, to: 13 }
];

export const stageOf = status => {
  const n = statusNumber(status);
  return n === '' ? null : STAGES.find(s => Number(n) >= s.from && Number(n) <= s.to)?.key ?? null;
};

export function filterCandidates(candidates, { mode, serviceLine, status }) {
  return candidates.filter(c =>
    (mode !== 'active' || c.ActiveProcess === 'Yes') &&
    (!serviceLine || c.ServiceLine === serviceLine) &&
    (!status || c.Status === status)
  );
}

// Counts per value, largest first; blanks are grouped under `emptyLabel`.
export function countBy(candidates, field, emptyLabel = 'Not specified') {
  const counts = new Map();
  candidates.forEach(c => {
    const key = c[field] || emptyLabel;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => (a.label === emptyLabel) - (b.label === emptyLabel) || b.count - a.count || a.label.localeCompare(b.label));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// '2026-02-05' -> '05 Feb 2026' (parsed by hand so the timezone can't shift the day).
export function formatDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d} ${MONTHS[Number(m) - 1]} ${y}`;
}

// '2026-06-01' -> 'Jun 26'
export function formatMonth(iso) {
  const [y, m] = iso.split('-');
  return `${MONTHS[Number(m) - 1]} ${y.slice(2)}`;
}

export const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
